// src/admin/SectionEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function SectionEditor({
  baseURL = "https://gantasoft.in/rankridgecollege/dev/v1",
  apiKey = "12345678",
  initial = {
    section_id: null,
    page_id: null,
    subpage_id: null,
    section_title: "",
    section_content: "",
    section_image: "",
    sort_order: 0,
    status: "active",
  },
  onClose,
  onSaved,
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [baseURL, apiKey]
  );

  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // dropdown data
  const [pages, setPages] = useState([]);
  const [subpages, setSubpages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingSubpages, setLoadingSubpages] = useState(false);

  // image upload state
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  // load pages
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPages(true);
        const { data } = await api.get("/pages", {
          params: { status: "active", sort: "page_id", order: "asc", limit: 1000 },
        });
        if (!cancelled) setPages(data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || "Failed to load pages");
      } finally {
        if (!cancelled) setLoadingPages(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // load subpages on page change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!form.page_id) {
        setSubpages([]);
        setForm(f => ({ ...f, subpage_id: null }));
        return;
      }
      try {
        setLoadingSubpages(true);
        const { data } = await api.get("/subpages", {
          params: { page_id: form.page_id, status: "active", sort: "subpage_id", order: "asc", limit: 1000 },
        });
        if (!cancelled) setSubpages(data?.items || []);
        if (!cancelled && form.subpage_id) {
          const ok = (data?.items || []).some(s => Number(s.subpage_id) === Number(form.subpage_id));
          if (!ok) setForm(f => ({ ...f, subpage_id: null }));
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || "Failed to load subpages");
      } finally {
        if (!cancelled) setLoadingSubpages(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, form.page_id]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.section_title.trim()) return "Section title is required.";
    if (!form.page_id && !form.subpage_id) return "Select a Page or a Subpage.";
    return "";
  };

  // Upload image to /upload
  const uploadImage = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // optional: tell backend where to store
      fd.append("dir", "sections");     // backend should place under /uploads/sections
      // optional: your own desired name (backend should sanitize)
      // fd.append("filename", slugify(form.section_title) + ".jpg");

      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.success && data?.path) {
        upd("section_image", data.path);
        setFile(null);
      } else {
        throw new Error(data?.message || "Upload failed");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        page_id: form.page_id ?? null,
        subpage_id: form.subpage_id ?? null,
        section_title: form.section_title,
        section_content: form.section_content || "",
        section_image: form.section_image || "",
        sort_order: Number(form.sort_order) || 0,
        status: form.status || "active",
      };
      if (form.section_id) {
        await api.put(`/sections/${form.section_id}`, { section_id: form.section_id, ...payload });
      } else {
        await api.post(`/sections`, payload);
      }
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">{form.section_id ? "Edit Section" : "Add Section"}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {/* Body */}
        <div className="p-4 grid gap-4">
          {error && <div className="px-3 py-2 text-sm rounded bg-red-50 text-red-700 border border-red-200">{error}</div>}

          {/* Page/Subpage pickers */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Page</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.page_id ?? ""}
                onChange={(e)=>upd("page_id", e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— Select Page —</option>
                {loadingPages ? <option disabled>Loading…</option> :
                  pages.map(p => <option key={p.page_id} value={p.page_id}>[{p.page_id}] {p.page_name} — {p.page_slug}</option>)
                }
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700">Subpage (optional)</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.subpage_id ?? ""}
                onChange={(e)=>upd("subpage_id", e.target.value ? Number(e.target.value) : null)}
                disabled={!form.page_id || loadingSubpages}
              >
                <option value="">
                  {form.page_id ? (loadingSubpages ? "Loading…" : "— Select Subpage —") : "Select a Page first"}
                </option>
                {subpages.map(sp => (
                  <option key={sp.subpage_id} value={sp.subpage_id}>
                    [{sp.subpage_id}] {sp.subpage_name} — {sp.subpage_slug}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Title / Sort / Status */}
          <div>
            <label className="text-sm text-gray-700">Section Title *</label>
            <input
              className="border rounded px-3 py-2 w-full"
              value={form.section_title}
              onChange={(e)=>upd("section_title", e.target.value)}
              placeholder="e.g., Why Choose Rankridge"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Sort Order</label>
              <input
                type="number"
                className="border rounded px-3 py-2 w-full"
                value={form.sort_order ?? 0}
                onChange={(e)=>upd("sort_order", Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">Status</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={form.status}
                onChange={(e)=>upd("status", e.target.value)}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="deleted">deleted</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="text-sm text-gray-700">Section Content</label>
            <textarea
              rows={8}
              className="border rounded px-3 py-2 w-full"
              value={form.section_content || ""}
              onChange={(e)=>upd("section_content", e.target.value)}
              placeholder="Write content shown in this section"
            />
          </div>

          {/* Image upload */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-700">Image (URL)</label>
              <input
                className="border rounded px-3 py-2 w-full"
                value={form.section_image || ""}
                onChange={(e)=>upd("section_image", e.target.value)}
                placeholder="/uploads/sections/why.jpg"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can paste a URL or use the uploader to create one.
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-700">Upload New Image</label>
              <input
                type="file"
                accept="image/*"
                className="block w-full text-sm"
                onChange={(e)=>setFile(e.target.files?.[0] || null)}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={uploadImage}
                  disabled={!file || uploading}
                  className="px-3 py-1.5 rounded bg-gray-800 text-white disabled:opacity-50"
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
                {file && <span className="text-xs text-gray-600">{file.name}</span>}
              </div>
            </div>
          </div>

          {form.section_image && (
            <div>
              <label className="text-sm text-gray-700">Preview</label>
              <div className="mt-2 aspect-video bg-gray-50 border rounded overflow-hidden">
                <img src={"https://gantasoft.in/rankridgeschool/dev/"+ form.section_image} alt={form.section_title} className="w-full h-full object-cover" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded bg-green-700 text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200">Cancel</button>
        </div>
      </div>
    </div>
  );
}
